import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Section, ProductScreenshots, DiscordButton } from '../components';
import { products, discordLink } from '../data';
import { Image } from '../components/common/Image';
import { AppStoreButton } from '../components/common/AppStoreButton';

export const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const product = products.find((p) => p.id === id);

  const getYoutubeEmbedUrl = (rawUrl: string): string | null => {
    try {
      const url = new URL(rawUrl);
      const host = url.hostname.replace('www.', '');
      let videoId: string | null = null;

      if (host === 'youtu.be') {
        videoId = url.pathname.split('/').filter(Boolean)[0] ?? null;
      } else if (host.endsWith('youtube.com')) {
        if (url.pathname === '/watch') {
          videoId = url.searchParams.get('v');
        } else if (url.pathname.startsWith('/shorts/')) {
          videoId = url.pathname.split('/')[2] ?? null;
        } else if (url.pathname.startsWith('/embed/')) {
          videoId = url.pathname.split('/')[2] ?? null;
        }
      }

      if (!videoId) {
        return null;
      }

      const embedParams = new URLSearchParams();
      const clipId = url.searchParams.get('clip');
      const start = url.searchParams.get('start');
      const end = url.searchParams.get('end');

      if (clipId) {
        embedParams.set('clip', clipId);
      }
      if (start) {
        embedParams.set('start', start);
      }
      if (end) {
        embedParams.set('end', end);
      }

      const query = embedParams.toString();
      return `https://www.youtube.com/embed/${videoId}${query ? `?${query}` : ''}`;
    } catch {
      return null;
    }
  };

  if (!product) {
    return <Navigate to="/404" replace />;
  }

  // Product-specific accent colors
  let accentColor = '#E5C65A';
  let accentGlow = 'rgba(229, 198, 90, 0.1)';
  if (product.id === 'raven') {
    accentColor = '#4FA3D1';
    accentGlow = 'rgba(79, 163, 209, 0.1)';
  } else if (product.id === 'chordpond') {
    accentColor = '#5BBFA8';
    accentGlow = 'rgba(91, 191, 168, 0.1)';
  }

  return (
    <>
      <Helmet>
        <title>{product.name} - FERAL-AUD.IO</title>
        <meta name="description" content={product.description} />
      </Helmet>

      {/* Product Hero */}
      <div className="min-h-[80vh] bg-deepBlack text-white flex items-center justify-center relative overflow-hidden pt-20 pb-20">
        <div className="absolute inset-0 opacity-20" style={{ background: `radial-gradient(circle, ${accentGlow}, transparent, transparent)` }} />
        
        <div className="max-w-7xl mx-auto px-6 relative z-10 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column: Huge Logo */}
            <div className="flex justify-center lg:justify-end order-2 lg:order-1 animate-hero-logo">
              <Image
                src={product.logo.url}
                alt={product.logo.alt}
                className={`w-full max-w-[500px] lg:max-w-none h-auto opacity-90${product.id === 'chordpond' ? ' rounded-[22%]' : ''}`}
                style={{ color: accentColor, filter: `drop-shadow(0 0 30px ${accentGlow})` }}
              />
            </div>

            {/* Right Column: Content */}
            <div className="text-center lg:text-left order-1 lg:order-2">
              <h1 className="text-6xl md:text-8xl font-black tracking-tight text-white mb-6 font-mono animate-hero-text">
                {product.name}
              </h1>
              
              <p className="text-2xl md:text-3xl font-semibold mb-8 leading-tight animate-hero-text" style={{ color: accentColor, animationDelay: '0.1s' }}>
                {product.tagline}
              </p>
              
              <p className="text-lg md:text-xl text-gray-300 leading-relaxed mb-10 max-w-2xl mx-auto lg:mx-0 animate-hero-text" style={{ animationDelay: '0.2s' }}>
                {product.description}
              </p>

              {product.appStoreUrl ? (
                <div className="flex justify-center lg:justify-start mt-8 animate-hero-button">
                  <AppStoreButton url={product.appStoreUrl} />
                </div>
              ) : (
                <div className="flex justify-center lg:justify-start mt-8 animate-hero-button">
                  <span className="text-sm uppercase tracking-widest font-mono" style={{ color: accentColor }}>Coming Soon</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Hero Animations */}
      <style>{`
        @keyframes hero-text {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes hero-logo {
          0% { opacity: 0; transform: scale(0.97); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes hero-button {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        .animate-hero-text {
          animation: hero-text 0.8s ease-out forwards;
          opacity: 0;
        }
        .animate-hero-logo {
          animation: hero-logo 1s ease-out forwards;
          opacity: 0;
        }
        .animate-hero-button {
          animation: hero-button 0.6s ease-out 0.4s forwards;
          opacity: 0;
        }
      `}</style>

      {/* Features List */}
      {product.features && product.features.length > 0 && (
        <Section background="gray" className="py-16">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-white font-mono mb-10 text-center">
              FEATURES
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {product.features.map((feature, index) => (
                <div key={index} className="flex items-start">
                  <span className="mr-3 mt-1 text-lg" style={{ color: accentColor }}>•</span>
                  <p className="text-gray-300 leading-relaxed">{feature}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>
      )}

      {/* Community Videos */}
      {product.videos && product.videos.length > 0 && (
        <Section background="black" id="videos" className="py-16 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-4 font-mono">
              {product.videosTitle ?? 'COMMUNITY VIDEOS'}
            </h2>
            <p className="text-center text-gray-400 mb-12 max-w-2xl mx-auto">
              {product.videosDescription ?? `Check out what the community is creating with ${product.name}`}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {product.videos.map((video, index) => {
                const embedUrl = getYoutubeEmbedUrl(video.url);
                return (
                  <div key={index} className="space-y-4">
                    <div className="relative aspect-video bg-black rounded-lg overflow-hidden shadow-2xl">
                      {embedUrl ? (
                        <iframe
                          src={embedUrl}
                          title={video.title}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="absolute inset-0 w-full h-full"
                        />
                      ) : (
                        <a
                          href={video.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="absolute inset-0 flex items-center justify-center text-sm uppercase tracking-wider font-mono text-white/80 hover:text-white transition-colors"
                        >
                          Watch on YouTube
                        </a>
                      )}
                    </div>
                    <div className="text-center">
                      <h3 className="text-lg font-semibold text-white mb-1">
                        {video.title}
                      </h3>
                      {video.description && (
                        <p className="text-sm text-gray-400">
                          {video.description}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Section>
      )}

      {/* Version History */}
      {product.versionHistory && product.versionHistory.length > 0 && (
        <Section background="gray" className="py-16 border-t border-white/5">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-white font-mono mb-10 text-center">
              WHAT'S NEW
            </h2>
            <div className="space-y-12">
              {product.versionHistory.map((version, index) => (
                <div key={index} className="relative">
                  <div className="flex items-center mb-6">
                    <h3 className="text-xl font-bold font-mono" style={{ color: accentColor }}>
                      Version {version.version}
                    </h3>
                    {version.date && (
                      <span className="ml-4 text-sm text-gray-500">
                        {version.date}
                      </span>
                    )}
                  </div>
                  {version.title && (
                    <p className="text-gray-400 italic mb-4 font-mono text-sm">
                      {version.title}
                    </p>
                  )}
                  <ul className="space-y-3">
                    {version.changes.map((change, changeIndex) => (
                      <li key={changeIndex} className="flex items-start">
                        <span className="mr-3 mt-1" style={{ color: accentColor }}>–</span>
                        <p className="text-gray-300 leading-relaxed">{change}</p>
                      </li>
                    ))}
                  </ul>
                  {version.note && (
                    <p className="mt-4 text-gray-400 italic pl-6">
                      {version.note}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </Section>
      )}

      {/* Screenshots / Interface */}
      {product.screenshots && product.screenshots.length > 0 && (
        <Section background="black" id="screenshots" className="py-16 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-16 font-mono">
              INTERFACE
            </h2>
            <ProductScreenshots
              screenshots={product.screenshots}
              productName={product.name}
              accentColor={accentColor}
            />
          </div>
        </Section>
      )}

      {/* Community Section */}
      {discordLink && (
        <Section background="gray" id="community" className="border-t border-white/5">
          <div className="text-center max-w-3xl mx-auto py-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 font-mono">
              JOIN THE COMMUNITY
            </h2>
            <p className="text-lg text-gray-400 mb-10 leading-relaxed">
              Connect with other musicians and sound designers. Share presets, get help, and provide feedback directly to the developers.
            </p>
            <DiscordButton url={discordLink.url} className="px-10" />
          </div>
        </Section>
      )}
    </>
  );
};
